/**
 * Validates individual address components and returns specific errors for each field
 * @param address - The address components to validate
 * @returns Object containing validation results and field-specific errors
 */
export const validateAddressComponents = (address) => {
    var _a, _b, _c, _d, _e;
    const errors = {
        venueName: '',
        streetAddress: '',
        unit: '',
        city: '',
        state: '',
        zipCode: ''
    };
    // Validate street address
    if ((_a = address.streetAddress) === null || _a === void 0 ? void 0 : _a.trim()) {
        if (address.streetAddress.length > 100) {
            errors.streetAddress = 'address too long';
        }
    }
    // Validate unit / floor / suite
    if (((_b = address.unit) === null || _b === void 0 ? void 0 : _b.trim()) && address.unit.length > 80) {
        errors.unit = 'unit too long';
    }
    // Validate city (required)
    if (!((_c = address.city) === null || _c === void 0 ? void 0 : _c.trim())) {
        errors.city = 'city required';
    }
    else if (!/^[A-Za-z\s\-'\.]+$/.test(address.city.trim())) {
        errors.city = 'invalid city format';
    }
    else if (address.city.length > 50) {
        errors.city = 'city too long';
    }
    // Validate state (required)
    if (!((_d = address.state) === null || _d === void 0 ? void 0 : _d.trim())) {
        errors.state = 'state required';
    }
    else if (!/^[A-Za-z\s\.]+$/.test(address.state.trim())) {
        errors.state = 'invalid state format';
    }
    else if (address.state.length > 50) {
        errors.state = 'state too long';
    }
    // Validate zip code (required)
    if (!((_e = address.zipCode) === null || _e === void 0 ? void 0 : _e.trim())) {
        errors.zipCode = 'zip code required';
    }
    else if (!/^\d{5}(-\d{4})?$/.test(address.zipCode.trim())) {
        errors.zipCode = 'invalid zip code format';
    }
    // Check if any errors exist
    const hasErrors = Object.keys(errors).some(key => errors[key] !== '');
    return {
        isValid: !hasErrors,
        errors
    };
};
/**
 * Parses an address string into its components
 * Expects format: "[venueName], [streetAddress], [city], [state] [zipCode]"
 * @param addressString - The address string to parse
 * @returns Parsed address components
 */
export const parseAddressString = (addressString) => {
    var _a, _b;
    if (typeof addressString !== 'string' || !addressString.trim()) {
        return { venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' };
    }
    const components = addressString.split(',').map(part => part.trim());
    // Initialize result
    const result = {
        venueName: '',
        streetAddress: '',
        unit: '',
        city: '',
        state: '',
        zipCode: ''
    };
    // Parse components from end to start to ensure we get state and zip correctly
    if (components.length > 0) {
        // Get state and zip from last component
        const lastComponent = components[components.length - 1];
        const stateZipMatch = lastComponent.match(/^([A-Za-z\s\.]+)\s+(\d{5}(-\d{4})?)$/);
        if (stateZipMatch) {
            result.state = stateZipMatch[1].trim();
            result.zipCode = stateZipMatch[2].trim();
            components.pop(); // Remove the state+zip component
        }
        // Get city from second to last component
        if (components.length > 0) {
            result.city = components[components.length - 1].trim();
            components.pop(); // Remove the city component
        }
        // Remaining components can be: street, venue+street, street+unit, or venue+street+unit.
        if (components.length >= 3) {
            result.unit = ((_a = components.pop()) === null || _a === void 0 ? void 0 : _a.trim()) || '';
            result.streetAddress = ((_b = components.pop()) === null || _b === void 0 ? void 0 : _b.trim()) || '';
            result.venueName = components.join(', ').trim();
        }
        else if (components.length === 2) {
            const [first, second] = components;
            if (/^\d+\s/.test(first.trim().toLowerCase())) {
                result.streetAddress = first.trim();
                result.unit = second.trim();
            }
            else {
                result.venueName = first.trim();
                result.streetAddress = second.trim();
            }
        }
        else if (components.length === 1) {
            result.streetAddress = components[0].trim();
        }
    }
    return result;
};
/**
 * Formats address components into a standardized string
 * @param address - The address components to format
 * @returns Formatted address string
 */
export const formatAddressComponents = (address) => {
    var _a, _b, _c, _d, _e, _f;
    const parts = [];
    // Add venue name if present
    if ((_a = address.venueName) === null || _a === void 0 ? void 0 : _a.trim()) {
        parts.push(address.venueName.trim());
    }
    // Add street address if present
    if ((_b = address.streetAddress) === null || _b === void 0 ? void 0 : _b.trim()) {
        parts.push(address.streetAddress.trim());
    }
    // Add apartment / floor / suite / room if present
    if ((_c = address.unit) === null || _c === void 0 ? void 0 : _c.trim()) {
        parts.push(address.unit.trim());
    }
    // Add city (required)
    if ((_d = address.city) === null || _d === void 0 ? void 0 : _d.trim()) {
        parts.push(address.city.trim());
    }
    // Add state and zip (required)
    if (((_e = address.state) === null || _e === void 0 ? void 0 : _e.trim()) && ((_f = address.zipCode) === null || _f === void 0 ? void 0 : _f.trim())) {
        parts.push(`${address.state.trim()} ${address.zipCode.trim()}`);
    }
    return parts.join(', ');
};
/**
 * Validates an address string format
 * @param addressString - The address string to validate
 * @returns Object containing validation results and field-specific errors
 */
export const validateAddressString = (addressString) => {
    // First parse the string into components
    const components = parseAddressString(addressString);
    // Then validate the components
    return validateAddressComponents(components);
};
//# sourceMappingURL=addressUtils.js.map